// native
import { Component, OnDestroy, OnInit } from '@angular/core';
import { DatePipe, TitleCasePipe } from '@angular/common';
import { Router } from '@angular/router';
import { BehaviorSubject, Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

// addon
import { TranslateService } from '@ngx-translate/core';

// services
import { DevicesService } from 'app/core/services/devices.service';
import { ExportTaskService } from 'app/core/services/export-task.service';
import { PreferencesService } from 'app/core/services/preferences.service';

// models
import { PaginatedItems, ListItemColumn, ListItemAction, ExportTask, ListItemMarker, ExportPerimetryTaskParameters } from '../models';

// constants
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, EXPORT_TASK_STATUS, EXPORT_TASK_TYPES } from '../constants';

interface ExportTaskDisplay extends ExportTask {
  testStrategyProtocol: string;
  startDate: string;
  endDate: string;
  deviceType: string;
}

@Component({
    selector: 'app-export-tasks',
    templateUrl: './export-tasks.component.html',
    standalone: false
})
export class ExportTasksComponent implements OnInit, OnDestroy {

  columns: ListItemColumn[] = [
    {
      translationKey: 'testStrategyProtocol',
      fieldName: 'testStrategyProtocol',
      fontClass: 'bold'
    },
    {
      translationKey: 'startDate',
      fieldName: 'startDate',
    },
    {
      translationKey: 'endDate',
      fieldName: 'endDate'
    },
    {
      translationKey: 'deviceType',
      fieldName: 'deviceType',
    },
    {
      translationKey: 'status',
      fieldName: 'displayedStatus',
      isSmall: true
    }
  ];

  actions: ListItemAction[] = [
    {
      translationKey: 'download',
      execute: (exportTask: ExportTask, action: ListItemAction) => this.download(exportTask, action),
      visible: (exportTask: ExportTask) => this.canDownload(exportTask)
    },
  ];

  greenMarker: ListItemMarker = {
    visible: (task: ExportTask) => task?.status === EXPORT_TASK_STATUS.FINISHED.value
  };

  orangeMarker: ListItemMarker = {
    visible: (task: ExportTask) => [EXPORT_TASK_STATUS.PENDING.value, EXPORT_TASK_STATUS.STARTED.value].includes(task?.status)
  };

  redMarker: ListItemMarker = {
    visible: (task: ExportTask) => [EXPORT_TASK_STATUS.FAILED.value, EXPORT_TASK_STATUS.EXPIRED.value].includes(task?.status)
  };

  exportTasks$: BehaviorSubject<PaginatedItems<ExportTask> | null> = new BehaviorSubject(null);

  currentPageSize: number = DEFAULT_PAGE_SIZE;
  currentPageIndex: number = DEFAULT_PAGE_INDEX;

  isLoading: boolean = false;
  pingInterval: any;

  languageSubscription: Subscription;

  constructor(
    private router: Router,
    private exportTaskService: ExportTaskService,
    private translateService: TranslateService,
    private preferencesService: PreferencesService,
    private devicesService: DevicesService
  ) { }

  ngOnInit() {
    this.getExportTasks(this.currentPageSize, this.currentPageIndex);
    this.setExportTaskPing();

    this.languageSubscription = this.translateService.onLangChange.subscribe(res => {
      this.getExportTasks(this.currentPageSize, this.currentPageIndex);
    });
  }

  private getExportTasks(pageSize: number, pageIndex: number, showLoader = true) {
    if (showLoader)
      this.isLoading = true;

    this.exportTaskService.getAll(pageSize, pageIndex).subscribe(res => {
      this.isLoading = false;

      this.exportTasks$.next(this.parseExportTasks(res));
    }, err => { this.isLoading = false; });
  }

  private setExportTaskPing() {
    this.pingInterval = setInterval(() => {
      this.getExportTasks(this.currentPageSize, this.currentPageIndex, false);
    }, 10000);
  }

  ngOnDestroy() {
    clearInterval(this.pingInterval);
    this.languageSubscription?.unsubscribe();
  }

  canDownload(exportTask: ExportTask): boolean {
    return exportTask.status === EXPORT_TASK_STATUS.FINISHED.value;
  }

  download(exportTask: ExportTask, action: ListItemAction) {
    action.isLoading = true;
    this.exportTaskService.download(exportTask.id).pipe(
      finalize(() => action.isLoading = false)
    ).subscribe();
  }

  create() {
    this.router.navigate(['/export-tasks/new']);
  }

  changePage(pageIndex: number, pageSize: number) {
    this.currentPageIndex = pageIndex;
    this.currentPageSize = pageSize;
    this.getExportTasks(this.currentPageSize, this.currentPageIndex);
  }

  private parseExportTasks(exportTasks: PaginatedItems<ExportTask>): PaginatedItems<ExportTaskDisplay> {
    const parsedExportTasks = exportTasks.results.map(exportTask => {

      const { start_date, end_date } = exportTask.parameters;
      const displayedStatus = new TitleCasePipe().transform(this.translateService.instant(EXPORT_TASK_STATUS[exportTask.status]?.translationKey));
      const formattedStartDate = new DatePipe('en-US').transform(start_date, this.preferencesService.defaultDateFormat.value);
      const formattedEndDate = new DatePipe('en-US').transform(end_date, this.preferencesService.defaultDateFormat.value);
      
      if (exportTask.type === EXPORT_TASK_TYPES.PERIMETRY) {
        const {
          device_type,
          test_strategy,
          test_protocol,
        } = exportTask.parameters as ExportPerimetryTaskParameters;

        return {
          ...exportTask,
          displayedStatus,
          testStrategyProtocol: `${this.getStrategyDisplayName(test_strategy)} : ${test_protocol}`,
          startDate: formattedStartDate,
          endDate: formattedEndDate,
          deviceType: this.devicesService.getDeviceTypeTranslatedName(device_type)
        };
      }

      return {
        ...exportTask,
        displayedStatus,
        testStrategyProtocol: this.exportTaskService.getExportTaskTypes().find(task => task.value === exportTask.type)?.name,
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        deviceType: "",
      };
    });

    return {
      results: parsedExportTasks,
      count: exportTasks.count
    };
  }

  private getStrategyDisplayName(strategyValue: string) {
    return this.exportTaskService.getStrategies().find(strategy => strategy.value === strategyValue)?.name || '';
  };
}
